import React, { useState } from "react";
import { ReportGrid } from "../../../components/ReportGrid";
import { Landmark, Wallet, Calendar, AlertCircle } from "lucide-react";
import { DatePicker } from "../../../components/DatePicker";

export const FinanceReports = () => {
  const [activeTab, setActiveTab] = useState<"liquidity" | "aging">("liquidity");
  
  // Eğer tarih filtresi kullanacaksan (Örn: Yaşlandırma için referans tarih)
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
 
const DateFilter = (
  <div className="flex items-center gap-2 no-print">
    <DatePicker
      label=""
      name="startDate"
      value={filters.startDate}
     className="min-w-48 "
      onChange={(e: any) => {
        const val = e?.target ? e.target.value : e; 
        setFilters(p => ({ ...p, startDate: val }));
      }} 
    /> 

    <span className="text-slate-300 font-light">/</span>
    
    <DatePicker
      label=""
      name="endDate" 
      value={filters.endDate}

     className="min-w-48"
      onChange={(e: any) => {
        const val = e?.target ? e.target.value : e;
        setFilters(p => ({ ...p, endDate: val }));
      }} 
    />   
  </div> 
);

  // Kolon Tanımları (Aynı kalıyor)
  const liquidityColumns = [
    { header: "Hesap Adı", field: "name" },
    { header: "Tür", body: (row: any) => (
      <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${row.iban ? "bg-blue-50 text-blue-600 border border-blue-100" : "bg-orange-50 text-orange-600 border border-orange-100"}`}>
        {row.iban ? "Banka" : "Kasa"}
      </span>
    )},
    { header: "Birim", field: "currency" },
    { header: "Bakiye", body: (row: any) => (
      <span className={`font-bold ${row.balance >= 0 ? "text-emerald-600" : "text-red-600"}`}>
        {row.balance?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {row.currency}
      </span>
    )},
  ];

  const agingColumns = [
    { 
      header: "Cari Bilgisi", 
      body: (row: any) => (
        <div className="py-1">
          <div className="font-bold  ">{row.accountName}</div>
          <div className="text-[10px]   font-mono tracking-tighter">REF: #{row.accountId}</div>
        </div>
      ) 
    },
    { 
      header: "Risk Durumu", 
      body: (row: any) => (
        <div className="text-right">
          <div className="font-black ">{row.currentBalance?.toLocaleString('tr-TR')} {row.currency}</div>
          <div className="text-[9px] text-red-500 font-bold uppercase">Toplam Alacak</div>
        </div>
      ) 
    },
    { 
      header: "Vade Detayları", 
      body: (row: any) => (
        <div className="flex flex-wrap gap-2 max-w-[400px]">
          {row.openInvoices?.length > 0 ? (
            row.openInvoices.map((inv: any, idx: number) => (
              <div key={idx} className="card2 p-2 text-[10px] flex gap-4 items-center">
                <div className="border-r pr-2">
                  <div className="font-bold  ">{inv.reference}</div>
                  <div className=" ">{new Date(inv.dueDate).toLocaleDateString('tr-TR')}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-indigo-600">{inv.openAmount.toLocaleString('tr-TR')} {inv.currency}</div>
                  <div className={inv.delayDays > 0 ? "text-red-500 font-black" : "text-emerald-500"}>
                    {inv.delayDays > 0 ? `${inv.delayDays} GÜN GECİKTİ` : "BEKLEMEDE"}
                  </div>
                </div>
              </div>
            ))
          ) : <span className="  italic text-[11px]">Açık fatura yok.</span>}
        </div>
      ) 
    },
  ];

  return (
    <div className="space-y-6">
      {/* ÜST BAŞLIK VE TABLAR */}
      <div className="flex flex-col card lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-sm sm:text-2xl font-black   flex items-center gap-3">
            <div className="p-2 bg-indigo-600 text-white rounded-xl  ">
              <Wallet size={20} />
            </div>
            Finansal Analiz & Raporlama
          </h1>
          <p className="text-sm   mt-1">İşletmenizin likidite durumunu ve alacak yaşlandırmasını takip edin.</p>
        </div>

        <div className="flex gap-1 p-1   card   rounded-xl w-fit no-print">
          <button 
            onClick={() => setActiveTab("liquidity")} 
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === "liquidity" ? "btn-new" : " "
            }`}
          >
            <Landmark size={16} /> Likidite
          </button>
          <button 
            onClick={() => setActiveTab("aging")} 
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === "aging" ? "btn-new" : "  "
            }`}
          >
            <AlertCircle size={16} /> Yaşlandırma
          </button>
        </div>
      </div>

      {/* RAPOR GRİDLERİ */}
      <div className="mt-4">
        {/* Likidite Raporu - Genelde tarih gerekmez anlık bakılır */}
        <div className={activeTab === "liquidity" ? "block" : "hidden"}>
          <ReportGrid 
            endpoint="/reports/finance/liquidity" 
            columns={liquidityColumns} 
            reportName="Nakit & Banka Likidite Raporu"
          />
        </div>
        
        {/* Yaşlandırma Raporu - Tarih filtresi ekledik */}
        <div className={activeTab === "aging" ? "block" : "hidden"}>
          <ReportGrid 
            endpoint="/reports/accounts/aging" 
            columns={agingColumns} 
            filters={filters}
            reportName="Cari Alacak Yaşlandırma (FIFO)"
            extraFilters={DateFilter}
          />
        </div>
      </div>
    </div>
  );
};